<template>
  <div
    v-if="stepData"
    class="step"
  >
    <component
      :is="question.questionComponent"
      v-for="question in questions"
      :key="question.questionId || question.questionComponent"
      product-name="bike"
      step-id="cover"
      :question-id="question.questionId"
      v-bind="question.params || {}"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="stepData"
      :show-confirm-modal="showConfirmModal"
      :drivers-backup="driversBackup"
      @toast="showToast($event)"
      @input="stepData = { ...stepData, ...$event }"
      @add-driver="$emit('add-driver')"
      @change-driver="$emit('change-driver', $event)"
      @delete-driver="$emit('delete-driver', $event)"
      @backup-additional-drivers="$emit('backup-additional-drivers', $event)"
      @restore-additional-drivers="$emit('restore-additional-drivers', $event)"
      @remove-additional-drivers="$emit('remove-additional-drivers')"
      @additional-drivers-status-check="hasAdditionalDrivers = $event"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { cloneDeep } from 'lodash';
import useExperimentMinimiseHelptext from '@/composables/useExperimentMinimiseHelptext';

export default {
  name: 'form-bike-step-3',
  props: {
    formObserver: {
      type: Object,
      required: true,
    },
    form: {
      type: Object,
      required: true,
    },
    driversBackup: {
      type: Array,
      default: null,
    },
    showConfirmModal: {
      type: Function,
      required: true,
    },
  },
  inject: ['constants', 'productName'],
  data () {
    return {
      stepData: null,
      hasAdditionalDrivers: this.form.drivers.length > 1,
    };
  },
  computed: {
    step () { return this.form.cover; },
    dateOfInsuranceStartHelpText () {
      const helpText = 'Prices from some insurance providers will often be lower further from your renewal date, compared to the prices in the few days leading up to your renewal. It’s good to make sure there are no gaps in your cover, so you’re always insured.</p>';
      const helpTextTitle = '<p><strong>Good to know</strong><br>';
      return {
        value: this.isExperimentMinimiseHelptext ? helpText : helpTextTitle + helpText,
        collapsible: this.isExperimentMinimiseHelptext,
        openerText: 'Renew Early to Lower Insurance Costs',
      };
    },
    insuranceEverDeclinedHelpText () {
      const helpText = 'This is usually for reasons such as non-disclosure of material or important facts, criminal convictions or misrepresentation by the proposer or any other person named on the policy etc.';
      const helpTextTitle = '<strong>Why are insurance policies cancelled or declined?</strong><br>';
      return {
        value: this.isExperimentMinimiseHelptext ? helpText : helpTextTitle + helpText,
        collapsible: this.isExperimentMinimiseHelptext,
        openerText: 'Reasons for Policy Cancellation or Declines',
      };
    },
    questions () {
      return [
        {
          questionComponent: 'question-additional-drivers',
          questionId: 'additional-drivers',
          params: {
            title: 'Would you like to add any additional riders to this policy?',
            helpText: 'You can add up to 4 additional riders to your quote.',
          },
        },
        {
          questionComponent: 'question-date-of-insurance-start',
          questionId: 'date-of-insurance-start',
          params: {
            helpText: this.dateOfInsuranceStartHelpText,
          },
        },
        {
          questionComponent: 'question-base-radio-list',
          questionId: 'type-of-cover',
          params: {
            customFields: {
              field: 'typeOfCover',
            },
            title: 'What type of cover would you like?',
            optionsConfig: {
              options: {
                1: {
                  text: 'Comprehensive',
                  helpText:
                    'Cover provided if your bike’s damaged, stolen or written off. You’ll be covered for claims made against you by other people too.',
                },
                2: {
                  text: 'Third Party, Fire & Theft',
                  helpText:
                    'If your bike’s stolen, or damaged by fire, you can claim. You’re also covered for claims made against you by other people.',
                },
                3: {
                  text: 'Third Party Only',
                  helpText:
                    'Cover for claims made against you by other people but there’s no cover for you or your bike.',
                },
              },
            },
            validationRules: {
              field: {
                required: true,
              },
            },
          },
        },
        {
          questionComponent: 'question-base-radio-list',
          questionId: 'type-of-use',
          params: {
            customFields: {
              field: 'typeOfUse',
            },
            title: 'What will you use the bike for?',
            helpText: {
              value: `
                <p>
                  <b>Social only</b> - day to day driving, but not including to and from a place of work.
                </p>
                <p>
                  <b>Social & Commuting</b> - day to day driving, including to and from a permanent place of work. This may not include any journeys that you might make while at work, such as driving to a client's office.
                </p>
                <p>
                  <b>Business Use</b> - to and from a permanent place of work and any journeys that you might make while at work, such as driving to a client's office. It will also Social use (day to day driving).
                </p>
                <p>
                  <b>Delivery Use</b> - this covers the main rider for use of the bike while working as a courier, delivery or messenger. It will not cover any taxi use.
                </p>
                <p>
                  Your employer will usually ask you to have Business Use of Delivery Use if it is needed for your job.
                </p>
              `,
              collapsible: true,
              openerText: 'Tell me more about these options',
            },
            optionsConfig: {
              options: [
                {
                  value: 5,
                  text: 'Social Only',
                  subtitle:
                    'N.B. Social, Domestic, Pleasure, excluding Commuting.',
                },
                {
                  value: 4,
                  text: 'Social & Commuting',
                  subtitle:
                    'N.B. Social, Domestic, Pleasure and Commuting  to 1 place of work.',
                },
                {
                  value: 1,
                  text: 'Business Use (by the proposer)',
                  subtitle:
                    'N.B. Also covers non-business use.',
                },
                {
                  value: 7,
                  text: 'Business Use (by the proposer & named riders)',
                  subtitle:
                    'N.B. Also covers non-business use.',
                },
                {
                  value: 9,
                  text: 'Delivery Use',
                  subtitle:
                    'Cover for courier/delivery riding. This will not cover you for taxi purposes (Private/Public hire). Please check cover details with your chosen provider before purchasing.',
                },
              ],
            },
            validationErrors: {
              field: {
                required: 'Please select your usage type.',
              },
            },
          },
        },
        {
          questionComponent: 'question-base-number',
          questionId: 'annual-mileage',
          params: {
            customFields: {
              field: 'annualMileage',
            },
            title: 'What is your annual mileage?',
            subtitle: 'Including any business mileage',
            helpText: {
              value: 'It\'s easiest to think about how many miles you drive per week and multiply this up over the year (which should also include longer journeys for weekend trips etc). If other drivers will have access to the vehicle, then include any miles that they might make.',
              collapsible: this.isExperimentMinimiseHelptext,
              openerText: 'Estimate annual mileage',
            },
            inputSuffix: 'miles',
            placeholder: 'e.g. 5000',
            maxLength: 6,
            validationRules: {
              field: {
                required: true,
                between: [1, 999999],
              },
            },
            validationErrors: {
              field: {
                required: 'Please enter your mileage e.g. 5000.',
                between: 'Please enter a numeric value between 1 and 999999.',
              },
            },
          },
        },
        {
          // TODO Need to investigate dynamic validation rules
          questionComponent: 'question-business-mileage',
          questionId: 'business-mileage',
          params: {
            showRules: {
              typeOfUse: {
                isAny: {
                  other: ['1', '7', '9'],
                },
                required: true,
              },
            },
          },
        },
        {
          questionComponent: 'question-base-dropdown',
          questionId: 'voluntary-excess',
          params: {
            customFields: {
              field: 'voluntaryExcess',
            },
            optionsConfig: {
              options: [
                {
                  value: 500,
                  text: '£ 500',
                },
                {
                  value: 450,
                  text: '£ 450',
                },
                {
                  value: 400,
                  text: '£ 400',
                },
                {
                  value: 350,
                  text: '£ 350',
                },
                {
                  value: 300,
                  text: '£ 300',
                },
                {
                  value: 250,
                  text: '£ 250',
                },
                {
                  value: 200,
                  text: '£ 200',
                },
                {
                  value: 150,
                  text: '£ 150',
                },
                {
                  value: 100,
                  text: '£ 100',
                },
                {
                  value: 50,
                  text: '£ 50',
                },
                {
                  value: 0,
                  text: 'None',
                },
              ],
            },
            title: 'What voluntary excess would you like on this policy?',
            helpText: `
              <p>
                Select the amount which you will pay if you make a claim. A higher voluntary excess normally reduces your premium. This will be <b>in addition</b> to any compulsory excesses added onto the policy.
              </p>
            `,
            validationRules: {
              field: {
                required: true,
              },
            },
          },
        },
        {
          questionComponent: 'question-no-claim-bonus',
          questionId: 'no-claims-bonus',
          params: {
            helpText: {
              title: `Your most recent insurance renewal notice should indicate how many years of NCD (no claims discount) you have earned. NCD is earned for each year you have held ${this.productName} insurance in your own name without making a claim.`,
              value: `If your NCD was earned on a company ${this.productName}, abroad (not in the UK) or if its more than 2 years since you last used it, please check this will be valid with your chosen insurer before buying the policy. You can't use a NCD on more than one ${this.productName === 'car' ? this.productName : 'vehicle'} at a time.`,
              collapsible: true,
              openerText: this.isExperimentMinimiseHelptext ? 'Ensure Your NCD Is Valid' : 'Show More',
              closerText: this.isExperimentMinimiseHelptext ? 'Ensure Your NCD Is Valid' : 'Hide',
            },
          },
        },
        {
          questionComponent: 'question-protect-bonus',
          questionId: 'protect-bonus',
          params: {
            titleAdditional: 'Do you wish to protect your NCD?',
            helpText: {
              value: 'No claims discount protection does not protect the overall price of your insurance policy. The price of your insurance policy may increase following an accident even if you were not at fault.If you have had a break in your previous insurance cover, your NCD may not be accepted by the insurance provider. Please contact the insurance provider directly regarding this',
              collapsible: true,
              openerText: 'No claims discount protection varies between insurance providers but in general it allows you to make one or more claims before your number of no claims bonus years falls (further information will be provided when you select a policy and proceed with the purchase of the policy on the insurance provider’s website).',
            },
          },
        },
        {
          questionComponent: 'question-base-radio-boolean',
          questionId: 'pillion-passenger',
          params: {
            customFields: {
              field: 'pillionPassenger',
            },
            title: 'Will you ever carry a pillion passenger?',
            helpText: 'i.e. a person who travels in a seat or place behind the rider.',
            validationRules: {
              field: {
                required: true,
              },
            },
          },
        },
        {
          questionComponent: 'question-main-user-of-vehicle',
          questionId: 'main-user-of-vehicle',
        },
        {
          questionComponent: 'question-owned-registered',
          questionId: 'owner-registered',
        },
        {
          questionComponent: 'question-base-radio-boolean',
          questionId: 'pay-insurance-monthly',
          params: {
            customFields: {
              field: 'payInsuranceMonthly',
            },
            title: 'Do you normally pay for your insurance monthly?',
            helpText: {
              value: '<p>Paying via monthly instalments allows you to spread the cost over the year, but you may end up paying more in total.</p><p>Paying annually may mean you pay less overall for the policy by avoiding any credit fees.</p>',
              collapsible: this.isExperimentMinimiseHelptext,
              openerText: 'Monthly vs. Annual Payments',
            },
            validationRules: {
              field: {
                required: true,
              },
            },
          },
        },
        {
          questionComponent: 'question-base-radio-boolean',
          questionId: 'insurance-ever-declined',
          params: {
            customFields: {
              field: 'insuranceEverDeclined',
            },
            title: 'Have you or any rider ever had insurance declined, cancelled, voided or special terms imposed?',
            helpText: this.insuranceEverDeclinedHelpText,
            validationRules: {
              field: {
                required: true,
              },
            },
          },
        },
        {
          questionComponent: 'question-password',
        },
        {
          questionComponent: 'question-outbound',
          questionId: 'outbound',
        },
        {
          questionComponent: 'question-direct-marketing-options',
          questionId: 'direct-marketing-options',
        },
        {
          questionComponent: 'question-user-keep-sign-in',
          questionId: 'user-keep-sign-in',
        },
        {
          questionComponent: 'question-terms-and-conditions',
          questionId: 'terms-and-conditions',
        },
      ];
    },
  },
  watch: {
    stepData: {
      handler (val) {
        this.$emit('step-data', val);
      },
      immediate: true,
    },
    'formObserver.valid': {
      immediate: true,
      handler (val) {
        if (val) this.$emit('valid');
      },
    },
  },
  async created () {
    this.$emit('tracking', 'cover');
    await this.initStep();
  },
  methods: {
    ...mapActions(['showToast']),
    async initQuotePassword () {
      const {
        firstName,
        surname: lastName,
        email,
        dateOfBirth: birthDate,
      } = this.form.drivers[0];

      try {
        const { status, data } = await this.$api.authUserQuote({
          firstName,
          lastName,
          email,
          birthDate,
        });
        if (status === 'success' && data.existingUser === false) {
          this.$set(this.stepData, 'userData', null);
          this.$set(this.stepData, '_userDataConfirm', null);
        } else {
          this.$delete(this.stepData, 'userData');
          this.$delete(this.stepData, '_userDataConfirm');
        }
      } catch (error) {
        this.$delete(this.stepData, 'userData');
        this.$delete(this.stepData, '_userDataConfirm');
        console.log(error);
      }
    },
    async initStep () {
      this.stepData = cloneDeep(this.form.cover);
      if (this.stepData.mainUserOfVehicle > this.form.drivers.length) {
        this.stepData.mainUserOfVehicle = 1;
      }
    },
  },
  setup () {
    // COMPOSABLES
    const { isExperimentMinimiseHelptext } = useExperimentMinimiseHelptext();

    return {
      isExperimentMinimiseHelptext,
    };
  },
};
</script>
